import { useCallback, useState } from 'react';
import { getBillingActionError, type BillingActionError } from '../../api';
import { useBillingSummary } from '../../hooks';
import type { BillingSummary } from '../../types';
import type { BillingPlanStatus, BillingViewModel, UseBillingViewModelArgs } from './Billing.ViewModel.types';

const TRIALING_STATUS = 'trialing';

const getPlanStatus = (summary: BillingSummary | undefined): { status: BillingPlanStatus | null; date: string | null } => {
  if (!summary) return { status: null, date: null };
  if (summary.subscriptionStatus === TRIALING_STATUS && summary.trialEnd) return { status: 'trialEnds', date: summary.trialEnd };
  if (summary.hasSubscription && summary.currentPeriodEnd) {
    return { status: summary.cancelAtPeriodEnd ? 'ends' : 'renews', date: summary.currentPeriodEnd };
  }
  if (summary.source === 'grant') {
    return summary.grantValidTo ? { status: 'grantUntil', date: summary.grantValidTo } : { status: 'grant', date: null };
  }
  return { status: null, date: null };
};

/**
 * The Plan & billing section: the current plan, what it allows and what is used, and the way into the
 * Stripe Customer Portal.
 */
export function useBillingViewModel({ billingApi, redirect }: UseBillingViewModelArgs): BillingViewModel {
  const { data: summary, isLoading, isError, refetch } = useBillingSummary(billingApi);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [portalError, setPortalError] = useState<BillingActionError | null>(null);

  const openPortal = useCallback(async () => {
    setIsOpeningPortal(true);
    setPortalError(null);
    try {
      const { url } = await billingApi.openPortal();
      redirect(url);
    } catch (error) {
      setPortalError(getBillingActionError(error));
      setIsOpeningPortal(false);
    }
  }, [billingApi, redirect]);

  const refresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const planStatus = getPlanStatus(summary);

  return {
    isLoading,
    hasLoadError: isError,
    isBillingEnabled: summary?.billingEnabled ?? false,
    planCode: summary?.planCode ?? 'free',
    planSource: summary?.source ?? 'free',
    hasSubscription: summary?.hasSubscription ?? false,
    isTrialing: summary?.subscriptionStatus === TRIALING_STATUS,
    trialEnd: summary?.trialEnd ?? null,
    isPastDue: summary?.isPastDue ?? false,
    cancelAtPeriodEnd: summary?.cancelAtPeriodEnd ?? false,
    currentPeriodEnd: summary?.currentPeriodEnd ?? null,
    grantValidTo: summary?.grantValidTo ?? null,
    planStatus: planStatus.status,
    planStatusDate: planStatus.date,
    isStale: summary?.isStale ?? false,
    isPlanTrialAvailable: summary?.planTrialAvailable ?? false,
    ownedBoards: summary?.usage?.ownedBoards ?? 0,
    maxOwnedBoards: summary?.limits?.maxOwnedBoards ?? null,
    professionalSourceTypes: summary?.usage?.professionalSourceTypes ?? [],
    maxProfessionalSources: summary?.limits?.maxProfessionalSources ?? null,
    sourceTrials: summary?.sourceTrials ?? [],
    isOpeningPortal,
    portalError,
    openPortal,
    refresh,
  };
}
